import React, { forwardRef } from 'react';
import './Button.scss';

const SIZE_CLASS = {
  xs: 'reops-button--xs',
  sm: 'reops-button--sm',
  md: 'reops-button--md',
  lg: 'reops-button--lg',
};

const VARIANT_CLASS = {
  primary: 'reops-button--primary',
  solid: 'reops-button--primary',
  secondary: 'reops-button--secondary',
  outline: 'reops-button--secondary',
  ghost: 'reops-button--ghost',
  subtle: 'reops-button--ghost',
  danger: 'reops-button--danger',
};

const Button = forwardRef(({
  as: Component = 'button',
  type = 'button',
  variant = 'primary',
  size = 'md',
  label,
  children,
  leftIcon,
  rightIcon,
  loading = false,
  loadingText,
  disabled = false,
  fullWidth = false,
  grow = false,
  iconOnly = false,
  className = '',
  onClick,
  ...props
}, ref) => {
  const isNativeButton = Component === 'button';
  const isDisabled = disabled || loading;
  const content = loading && loadingText ? loadingText : children || label;

  const handleClick = (event) => {
    if (isDisabled && !isNativeButton) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  const classes = [
    'reops-button',
    VARIANT_CLASS[variant] || VARIANT_CLASS.primary,
    SIZE_CLASS[size] || SIZE_CLASS.md,
    fullWidth ? 'reops-button--full' : '',
    grow ? 'reops-button--grow' : '',
    iconOnly || (!content && (leftIcon || rightIcon)) ? 'reops-button--icon-only' : '',
    loading ? 'reops-button--loading' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <Component
      ref={ref}
      className={classes}
      onClick={handleClick}
      disabled={isNativeButton ? isDisabled : undefined}
      aria-disabled={!isNativeButton && isDisabled ? true : undefined}
      type={isNativeButton ? type : undefined}
      {...props}
    >
      {loading ? <span className="reops-button__spinner" aria-hidden="true" /> : null}
      {!loading && leftIcon ? <span className="reops-button__icon" aria-hidden="true">{leftIcon}</span> : null}
      {content ? <span className="reops-button__label">{content}</span> : null}
      {!loading && rightIcon ? <span className="reops-button__icon" aria-hidden="true">{rightIcon}</span> : null}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
